import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Chip, Avatar, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Alert, CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import type { User } from '../types';

const ROLES = ['org_admin', 'assessor', 'icc_member', 'viewer'];

const roleColor: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning'> = {
  super_admin: 'secondary',
  org_admin: 'primary',
  assessor: 'success',
  icc_member: 'warning',
  viewer: 'default',
};

interface InvitePayload { full_name: string; email: string; role: string; organisation_id: number | ''; }

interface OrgOption { id: number; name: string; }

export default function UserManagementPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editRole, setEditRole] = useState('');

  // Invite dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState<InvitePayload>({ full_name: '', email: '', role: 'assessor', organisation_id: '' });
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [usersRes, orgsRes] = await Promise.all([
        api.get('/api/v1/users'),
        api.get('/api/v1/organisations/'),
      ]);
      setUsers(usersRes.data);
      setOrgs(orgsRes.data.map((o: any) => ({ id: o.id, name: o.name })));
    } catch {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const openEdit = (u: User) => {
    setEditUser(u);
    setEditRole(u.role);
    setEditOpen(true);
  };

  const saveRole = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      await api.patch(`/api/v1/users/${editUser.id}/role`, { role: editRole });
      setUsers(prev => prev.map(u => u.id === editUser.id ? { ...u, role: editRole as User['role'] } : u));
      setEditOpen(false);
    } catch {
      setError('Failed to update role.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u: User) => {
    try {
      await api.patch(`/api/v1/users/${u.id}/status`, { is_active: !u.is_active });
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, is_active: !x.is_active } : x));
    } catch {
      setError('Failed to update user status.');
    }
  };

  const sendInvite = async () => {
    setInviteError('');
    if (!invite.email || !invite.full_name) {
      setInviteError('Name and email are required.');
      return;
    }
    setSaving(true);
    try {
      const payload: any = { ...invite };
      if (payload.organisation_id === '') delete payload.organisation_id;
      const res = await api.post('/api/v1/users/invite', payload);
      setUsers(prev => [...prev, res.data]);
      setInviteSuccess(`Invitation sent to ${invite.email}`);
      setInvite({ full_name: '', email: '', role: 'assessor', organisation_id: '' });
      setTimeout(() => { setInviteOpen(false); setInviteSuccess(''); }, 2000);
    } catch (e: any) {
      setInviteError(e.response?.data?.detail || 'Failed to send invite.');
    } finally {
      setSaving(false);
    }
  };

  const canManage = me?.role === 'super_admin' || me?.role === 'org_admin';

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>User Management</Typography>
          <Typography variant="body2" color="text.secondary">Manage organisation users and their access roles.</Typography>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setInviteOpen(true)}>
            Invite User
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
                {canManage && <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 14 }}>
                        {u.full_name?.[0]?.toUpperCase() ?? 'U'}
                      </Avatar>
                      <Typography variant="body2" fontWeight={500}>{u.full_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{u.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={u.role.replace('_', ' ')}
                      color={roleColor[u.role] ?? 'default'}
                      size="small"
                      sx={{ textTransform: 'capitalize', fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={u.is_active ? 'Active' : 'Inactive'}
                      color={u.is_active ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                    </Typography>
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      {u.id !== me?.id && (
                        <>
                          <Tooltip title="Edit role">
                            <IconButton size="small" onClick={() => openEdit(u)}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title={u.is_active ? 'Deactivate' : 'Activate'}>
                            <IconButton size="small" color={u.is_active ? 'warning' : 'success'} onClick={() => toggleActive(u)}>
                              {u.is_active ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Role Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Role — {editUser?.full_name}</DialogTitle>
        <DialogContent>
          <TextField
            select fullWidth label="Role" value={editRole}
            onChange={e => setEditRole(e.target.value)}
            sx={{ mt: 1 }}
          >
            {ROLES.map(r => (
              <MenuItem key={r} value={r}>{r.replace('_', ' ')}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveRole} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite New User</DialogTitle>
        <DialogContent>
          {inviteSuccess && <Alert severity="success" sx={{ mb: 2 }}>{inviteSuccess}</Alert>}
          {inviteError && <Alert severity="error" sx={{ mb: 2 }}>{inviteError}</Alert>}
          <TextField
            fullWidth label="Full Name" value={invite.full_name}
            onChange={e => setInvite(p => ({ ...p, full_name: e.target.value }))}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth label="Email" type="email" value={invite.email}
            onChange={e => setInvite(p => ({ ...p, email: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            select fullWidth label="Role" value={invite.role}
            onChange={e => setInvite(p => ({ ...p, role: e.target.value }))}
            sx={{ mb: me?.role === 'super_admin' ? 2 : 0 }}
          >
            {ROLES.map(r => (
              <MenuItem key={r} value={r}>{r.replace('_', ' ')}</MenuItem>
            ))}
          </TextField>
          {me?.role === 'super_admin' && (
            <TextField
              select fullWidth label="Organisation"
              value={invite.organisation_id}
              onChange={e => setInvite(p => ({ ...p, organisation_id: e.target.value === '' ? '' : Number(e.target.value) }))}
            >
              <MenuItem value=""><em>None (unassigned)</em></MenuItem>
              {orgs.map(o => (
                <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={sendInvite} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Send Invite'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
